import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { LanguageProvider } from "./hooks/useLanguage";
import { PayrollConfigProvider } from "./hooks/usePayrollConfig";
import { signOut } from "firebase/auth";
import { auth } from "./firebase/config";

import LoginPage from "./pages/LoginPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminPromoters from "./pages/admin/AdminPromoters";
import AdminSales from "./pages/admin/AdminSales";
import AdminSalary from "./pages/admin/AdminSalary";
import AdminAttendance from "./pages/admin/AdminAttendance";
import AdminTargets from "./pages/admin/AdminTargets";
import AdminSKUs from "./pages/admin/AdminSKUs";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import SalesReportPage from "./pages/shared/SalesReportPage";
import PromoterLayout from "./pages/promoter/PromoterLayout";
import PromoterHome from "./pages/promoter/PromoterHome";
import PromoterCheckin from "./pages/promoter/PromoterCheckin";
import PromoterSales from "./pages/promoter/PromoterSales";
import { PromoterSKU } from "./pages/promoter/PromoterSKU";
import PromoterNotifications from "./pages/promoter/PromoterNotifications";
import SupervisorLayout from "./pages/supervisor/SupervisorLayout";
import SupervisorDashboard from "./pages/supervisor/SupervisorDashboard";
import SupervisorSales from "./pages/supervisor/SupervisorSales";
import SupervisorAttendance from "./pages/supervisor/SupervisorAttendance";
import SupervisorTargets from "./pages/supervisor/SupervisorTargets";
import SupervisorSalary from "./pages/supervisor/SupervisorSalary";
import SupervisorSKU from "./pages/supervisor/SupervisorSKU";
import SupervisorNotifications from "./pages/supervisor/SupervisorNotifications";
import SupervisorNotifSend from "./pages/supervisor/SupervisorNotifSend";
import TrainingPage from "./pages/shared/TrainingPage";
import BonusProgramsPage from "./pages/shared/BonusProgramsPage";

function AppRoutes() {
  const { user, profile, loading } = useAuth();

  useEffect(() => {
    if (!loading && user && profile !== undefined && !profile?.role) {
      signOut(auth).catch(() => {});
    }
  }, [loading, user, profile]);

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>
      Đang tải...
    </div>
  );

  if (!user) return <LoginPage />;

  if (profile?.role === "admin") {
    return (
      <Routes>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="promoters" element={<AdminPromoters />} />
          <Route path="sales" element={<AdminSales />} />
          <Route path="reports" element={<SalesReportPage role="admin" />} />
          <Route path="attendance" element={<AdminAttendance />} />
          <Route path="salary" element={<AdminSalary />} />
          <Route path="targets" element={<AdminTargets />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="holidays" element={<Navigate to="/admin/settings?tab=holiday" replace />} />
          <Route path="skus" element={<AdminSKUs />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="bonus-rules" element={<Navigate to="/admin/settings?tab=bonus" replace />} />
          <Route path="training" element={<TrainingPage />} />
          <Route path="audit" element={<AdminAuditLog />} />
        </Route>
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    );
  }

  if (profile?.role === "supervisor") {
    return (
      <Routes>
        <Route path="/supervisor" element={<SupervisorLayout />}>
          <Route index element={<SupervisorDashboard />} />
          <Route path="sales" element={<SupervisorSales />} />
          <Route path="reports" element={<SalesReportPage role="supervisor" />} />
          <Route path="attendance" element={<SupervisorAttendance />} />
          <Route path="targets" element={<SupervisorTargets />} />
          <Route path="salary" element={<SupervisorSalary />} />
          <Route path="sku" element={<SupervisorSKU />} />
          <Route path="notifications" element={<SupervisorNotifications />} />
          <Route path="notifications/send" element={<SupervisorNotifSend />} />
          <Route path="programs" element={<BonusProgramsPage role="supervisor" />} />
          <Route path="training" element={<TrainingPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/supervisor" replace />} />
      </Routes>
    );
  }

  if (profile?.role === "promoter") {
    return (
      <Routes>
        <Route path="/promoter" element={<PromoterLayout />}>
          <Route index element={<PromoterHome />} />
          <Route path="checkin" element={<PromoterCheckin />} />
          <Route path="sales" element={<PromoterSales />} />
          <Route path="reports" element={<SalesReportPage role="promoter" />} />
          <Route path="sku" element={<PromoterSKU />} />
          <Route path="training" element={<TrainingPage />} />
          <Route path="notifications" element={<PromoterNotifications />} />
          <Route path="programs" element={<BonusProgramsPage role="promoter" />} />
        </Route>
        <Route path="*" element={<Navigate to="/promoter" replace />} />
      </Routes>
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>
      Đang tải...
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <PayrollConfigProvider>
            <AppRoutes />
          </PayrollConfigProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
